import { useState, useEffect } from 'react'
import { handleSignedAgreement, updateSignedLease, getCustomerLease, getLeaseById } from '../../../api-utils'

/**
 * Resolve the lease this post-sign flow is about.
 * Prefer the exact lease referenced by `leaseId` (embedded in the eSignatures
 * redirect URL); fall back to the customer's most recent lease.
 */
async function resolveLease(leaseId: string | null, customerId: string) {
    if (leaseId) {
        const res = await getLeaseById(leaseId)
        if (res?.success && res.lease) return res.lease
    }
    const res = await getCustomerLease(customerId)
    if (res?.success && res.lease) return res.lease
    return null
}

/**
 * Hook to handle the post-signing lease PDF flow (no payment).
 *
 * Resolves the lease (and its eSignatures contract id) from PocketBase rather
 * than relying on a contract id in the redirect URL — eSignatures.io does not
 * document which query params it appends, so the previous URL-only approach
 * silently dead-ended and left leases stuck in `created` (never `signed`).
 *
 * 1. Resolve the lease (by lease_id, else by customer) → contract_ref_id
 * 2. If already signed, short-circuit (idempotent on reload)
 * 3. Fetch signed PDF from eSignatures.io, store it via update-signed-lease
 * 4. Re-fetch the lease to get the hosted file URL
 */
export function useLeasePdfFlow(
    hasSigned: boolean,
    leaseId: string | null,
    customerId: string | null,
    onLeaseReady: (leaseWithUrl: any) => void
) {
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | undefined>()

    useEffect(() => {
        if (!hasSigned || !customerId) return

        let cancelled = false
        setLoading(true)
        setError(undefined)

        const run = async () => {
            const lease = await resolveLease(leaseId, customerId)
            if (!lease) {
                throw new Error('Could not find your lease after signing. Please contact us so we can finish setting up your account.')
            }

            const contractId = lease.contract_ref_id
            if (!contractId || contractId === 'pending') {
                throw new Error('Your lease is missing its signing reference. Please contact us so we can finish setting up your account.')
            }

            // Already persisted (e.g. page reloaded) — nothing more to do.
            if (lease.signed_lease) {
                return lease
            }

            const contractResponse: any = await handleSignedAgreement(contractId)
            const signedPDF = contractResponse?.contract_pdf_url
            if (!signedPDF) {
                throw new Error('Your signed lease is still being generated. Please refresh in a moment.')
            }

            const now = new Date()
            const easternTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }))
            const updateResponse: any = await updateSignedLease(contractId, signedPDF, easternTime.toISOString())
            if (!updateResponse?.success) {
                throw new Error(updateResponse?.error || 'Failed to store signed lease PDF')
            }

            // Re-fetch so we get the hosted signed_lease file URL.
            const refreshed = await resolveLease(leaseId, customerId)
            return refreshed || lease
        }

        run()
            .then((lease) => {
                if (cancelled) return
                onLeaseReady(lease)
                setLoading(false)
            })
            .catch((err: any) => {
                if (cancelled) return
                console.error('Error in useLeasePdfFlow:', err)
                setError(err.message || 'An error occurred processing your signed lease')
                setLoading(false)
            })

        return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasSigned, leaseId, customerId])

    return { loading, error }
}
